import { unpinRecipe, useActiveRecipes } from './activeRecipes';
import { Link, paths, useRoute } from './router';
import {Button, Icon } from './ui';

/**
 * Barre des recettes en cuisson (US-35) : « je fais un plat et son
 * accompagnement en même temps ».
 *
 * Une seule chose à faire, et elle doit se faire d'un doigt gras : **passer
 * d'une recette à l'autre**. Chaque entrée est donc un lien direct vers le
 * mode cuisine de sa recette, à 48 px comme le reste de ce contexte (§4), et
 * la recette en cours est marquée plutôt que masquée — savoir où l'on est fait
 * partie de savoir où aller.
 *
 * ### La règle, en une phrase
 *
 * **La barre est visible exactement tant qu'au moins une recette est en
 * cuisson.** Rien d'autre ne la fait apparaître ni disparaître.
 *
 * Elle s'arrêtait auparavant à deux — « une barre de navigation entre un seul
 * élément n'aide personne » —, ce qui était juste tant qu'on ne la voyait que
 * comme un sélecteur. L'audit design du 26 août 2026 l'a rencontrée autrement
 * (F-10) : une barre qui apparaît et disparaît sans que l'écran dise pourquoi
 * se lit comme un état instable, et le seul cas où l'on ignore qu'une cuisson
 * est en cours est précisément celui d'**une** cuisson. La barre est donc
 * l'indicateur d'état d'abord, le sélecteur ensuite — à un élément, elle dit
 * « quelque chose cuit, voici où c'est » et y ramène d'une tape.
 */
export function ActiveRecipesBar(): React.JSX.Element | null {
  const active = useActiveRecipes();
  const route = useRoute();

  if (active.length === 0) return null;

  const currentId = 'recipeId' in route ? route.recipeId : null;

  return (
    <nav
      aria-label="Recettes en cuisson"
      data-testid="active-recipes"
      className="flex flex-wrap items-center gap-2 rounded-card border border-border bg-surface p-2"
    >
      <span className="text-label px-1">En cuisson</span>
      {active.map((recipe) => {
        const isCurrent = recipe.id === currentId;
        return (
          <span key={recipe.id} className="flex items-center gap-1">
            <Link
              to={paths.cook(recipe.id)}
              aria-current={isCurrent ? 'page' : undefined}
              className={`min-h-touch inline-flex items-center rounded-control px-3 text-sm font-medium no-underline ${
                isCurrent ? 'bg-primary text-primary-foreground' : 'border border-border-interactive text-foreground'
              }`}
            >
              {recipe.title}
            </Link>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Retirer ${recipe.title} des recettes en cuisson`}
              onClick={() => { unpinRecipe(recipe.id); }}
            >
              <Icon name="croix" />
            </Button>
          </span>
        );
      })}
    </nav>
  );
}
